import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'
import axios from 'axios'
import InfoIcon from '@mui/icons-material/Info'
import {
  Autocomplete,
  Box,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useFormik } from 'formik'
import FileUploadArea from '../../../components/File/FileUpload'
import FormErrorsModal from '../../../components/FormErrorsModal'
import SuccessModal from '../../../components/SuccessModal'
import Loading from '../../../components/Loading'
import EmailRow from '../components/EditEmailRow'
import CreateContactRow from '../components/CreateContactRow'
import EditPhoneRow from '../components/EditPhoneRow'
import EditLeadEmailRow from '../components/EditLeadEmailRow'
import EmailSettings from './EmailSettings'
import AddNewItem from '../components/AddNewItem'
import DialogWithUrlParams from '../components/DialogWithUrlParams'
import BoxWrapper from '../../../components/BoxWrapper'
import ReadPermissions from '../../AccountsAndPermissions/ReadPermissions'
import CustomerDetails from './CustomerDetails'
import FileViewRow from '../../../components/File/FileViewRow'
import axiosInstance from '../../../utils/axiosInstance'
import { CUSTOMER_TYPES, TcreatCustomerObject, USER_ROLES } from '../../../constants'
import { CustomTabPanel, a11yProps } from '../../../components/CustomTabPanel'
import { PermissionSettings } from '../../AccountsAndPermissions/PermissionSettings'
import CustomerStyleSection, { StyleData } from '../components/CustomerStyleSection'
import { TunsolvedAny } from '../../../types/unsolved'
import { Integrations } from './Integrations'
import {
  CustomerTypeValues,
  customerTypes,
  customerValidationSchema,
} from '../components/CreateCustomer'
import {
  setCloseCustomerModal,
  setIsEditActiveForDialog,
  setIsSaveForDialogTriggered,
  setTriggerCustomersRefetch,
} from '../../../store/slices/general'
import { fixImgSrc, hasPermission } from '../../../utils/tools'
import { validateFileSize } from '../../../utils/fileValidation'
import { useSelector } from '../../../store/store'

const CUSTOMER_ITEM_TABS = [
  { id: 0, label: 'Customer Details', path: '' },
  { id: 1, label: 'Integrations', path: '/integrations' },
  { id: 2, label: 'Email Settings', path: '/email-settings' },
  { id: 3, label: 'Customer Permission', path: '/customer-permission' },
] as const

interface EmailSettingData {
  id?: string
  createdAt?: Date | null
  updatedAt?: Date | null
  autoMailToIncomingLeads: boolean
  showDetailOnNewLeadEmail?: boolean
  emailTemplateBase: string
  inProgressText?: string
  rejectionText?: string
  successText?: string
  askForCVText?: string
  askForCvType?: 'upload' | 'custom'
  askForCvCustomText?: string
  autoAskForCvOnIncomingLeads?: boolean
  autoFollowUpEnabled?: boolean
  followUpDelayDays?: number
  followUpCustomTitle?: string
  followUpCustomText?: string
}

interface Iprops {
  businessCustomer?: string
}

const CustomerItem: React.FC<Iprops> = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [, setMutationLoading] = useState(false)
  const { general, user } = useSelector((state) => state)
  const { isSaveForDialogTriggered } = general
  const { role } = user.userInfo

  const formattedId = useMemo(() => {
    return location.pathname.split('/')[2]
  }, [location])

  const id = useMemo(() => {
    return formattedId
  }, [formattedId])

  const {
    data: fetchedData,
    isLoading,
    error,
  }: SWRResponse = useSWR(`/specificCustomerPermissions/${id}`, {
    fetcher: id ? axiosInstance : () => null,
  })
  const data = fetchedData?.data?.permissions

  // Fetch customer data (type for the dialog)
  const { data: customerData }: SWRResponse = useSWR(id ? `/customer/${id}` : null, axiosInstance)
  const customer = customerData?.data?.customer

  const [value, setValue] = useState(0)
  const [permissions, setPermissions] = useState(data)
  const [emailSettings, setEmailSettings] = useState<EmailSettingData | null>(null)

  const { isEditActiveForDialog } = useSelector((state) => state.general)

  const handleClose = () => {
    navigate('/customers')
    dispatch(setCloseCustomerModal())
  }

  const handleTabChange = (_e, v) => {
    setValue(v)
    const selectedTab = CUSTOMER_ITEM_TABS[v]
    navigate(`/customers/${id}${selectedTab.path}`)
  }

  const TabsContainer = (
    <Box>
      <Tabs value={value} onChange={handleTabChange} aria-label="basic tabs example">
        <Tab label="Customer Details" {...a11yProps(0)} />
        {role === USER_ROLES.ADMIN.value && <Tab label="Integrations" {...a11yProps(1)} />}
        {role === USER_ROLES.ADMIN.value && <Tab label="Email Settings" {...a11yProps(2)} />}
        {(role === USER_ROLES.ADMIN.value ||
          role === USER_ROLES.EXTERNAL_USER_WHITE_LABEL.value) && (
          <Tab
            label="Customer Permission"
            {...a11yProps(role === USER_ROLES.ADMIN.value ? 3 : 1)}
          />
        )}
      </Tabs>
    </Box>
  )

  useEffect(() => {
    if (data) setPermissions(data)
  }, [data])

  useEffect(() => {
    async function savePermissions() {
      try {
        setMutationLoading(true)
        await axiosInstance.post(`/specificCustomerPermissions/${id}`, { permissions })
        setSuccessModalOpen(true)
      } catch (error) {
        setErrorsModalOpen(true)
        setFormikErrors([String(error)])
      } finally {
        setMutationLoading(false)
      }
    }

    async function saveEmailSettings() {
      try {
        setMutationLoading(true)
        await axiosInstance.post(`/customer-email-settings/${id}`, emailSettings)
        setSuccessModalOpen(true)
      } catch (error) {
        setErrorsModalOpen(true)
        setFormikErrors([String(error)])
      } finally {
        setMutationLoading(false)
      }
    }

    const isPermissionsChanged = JSON.stringify(permissions) !== JSON.stringify(data)
    const isEmailSettingsChanged = emailSettings !== null

    if (isSaveForDialogTriggered && id) {
      if (isPermissionsChanged) {
        savePermissions()
      } else if (isEmailSettingsChanged) {
        saveEmailSettings()
      }
      dispatch(setIsSaveForDialogTriggered(false))
    }

    !id && window.location.reload()
  }, [
    id,
    isSaveForDialogTriggered,
    permissions,
    emailSettings,
    dispatch,
    data,
  ])

  useEffect(() => {
    const tabPath = location.pathname.split(`/customers/${id}`)[1] || ''
    const foundTab = CUSTOMER_ITEM_TABS.findIndex((tab) => tab.path === tabPath)
    if (foundTab >= 0) {
      setValue(foundTab)
    } else {
      setValue(0)
    }
  }, [id, location.pathname])

  useEffect(() => {
    const updateSearchParam = () => {
      if (isEditActiveForDialog) {
        navigate({ search: 'edit' })
      } else if (window.location.search) {
        dispatch(setIsEditActiveForDialog(true))
      } else if (location.pathname.includes('edit')) {
        navigate({ search: '' })
      }
    }
    updateSearchParam()
  }, [isEditActiveForDialog, location.pathname, navigate, dispatch])

  if (!permissions) return <FormattedMessage id="not-admin" />
  if (isLoading) return <Loading />
  if (error) return <div>{error.message}</div>

  return (
    <div>
      <DialogWithUrlParams type="customer" customerType={customer?.type}>
        {(id) => (
          <>
            <Container maxWidth="md" className="formContainer">
              <BoxWrapper>
                {TabsContainer}
                <div className="settingsCont marginTop">
                  <CustomTabPanel value={value} index={0}>
                    {isEditActiveForDialog ? (
                      <UpdateCustomer id={id} />
                    ) : (
                      <CustomerDetails id={id} />
                    )}
                  </CustomTabPanel>
                  {role === USER_ROLES.ADMIN.value && (
                    <CustomTabPanel value={value} index={1}>
                      <Integrations id={id} />
                    </CustomTabPanel>
                  )}
                  {role === USER_ROLES.ADMIN.value && (
                    <CustomTabPanel value={value} index={2}>
                      <EmailSettings
                        customerId={id}
                        isEditMode={isEditActiveForDialog}
                        onEmailSettingsChange={(settings) =>
                          setEmailSettings({ ...settings } as EmailSettingData)
                        }
                        customerType={customer?.type}
                      />
                    </CustomTabPanel>
                  )}
                  {(role === USER_ROLES.ADMIN.value ||
                    role === USER_ROLES.EXTERNAL_USER_WHITE_LABEL.value) && (
                    <CustomTabPanel value={value} index={role === USER_ROLES.ADMIN.value ? 3 : 1}>
                      <div className="customerDetails subFormContainer">
                        <div className="header">
                          <FormattedMessage id="customer-permissions" />
                        </div>
                        {isEditActiveForDialog ? (
                          <PermissionSettings data={permissions} setData={setPermissions} />
                        ) : (
                          <ReadPermissions permissions={permissions} />
                        )}
                      </div>
                    </CustomTabPanel>
                  )}
                </div>
              </BoxWrapper>
            </Container>
          </>
        )}
      </DialogWithUrlParams>
      <SuccessModal
        successModalOpen={successModalOpen}
        handleClose={handleClose}
        setSuccessModalOpen={setSuccessModalOpen}
        message="Updated successfully"
      />
      <FormErrorsModal
        errorsModalOpen={errorsModalOpen}
        formikErrors={formikErrors}
        setErrorsModalOpen={setErrorsModalOpen}
      />
    </div>
  )
}

export default CustomerItem

const UpdateCustomer = ({ id }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [customerType, setCustomerType] = useState<CustomerTypeValues>('partner')
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const [logoFile, setLogoFile] = useState<null | File>(null)
  const [logoFileName, setLogoFileName] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [addNewEmailClicked, setAddNewEmailClicked] = useState(false)
  const [addNewPhoneClicked, setAddNewPhoneClicked] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPhone, setNewPhone] = useState('')

  const { isSaveForDialogTriggered } = useSelector((state) => state.general)
  const { permissions, userInfo } = useSelector((state) => state.user)
  const permission = hasPermission(permissions?.customersDetailsFields)

  const { role } = userInfo
  const businessCustomer =
    role !== USER_ROLES.ADMIN.value && role !== USER_ROLES.INTERNAL_USER.value // todo

  const handleClose = () => {
    navigate('/customers')
    dispatch(setCloseCustomerModal())
  }

  const { data: fetchedCustomerDetails, isLoading: loadingCustomerDetails }: SWRResponse = useSWR(
    id ? `/customer/${id}` : null,
    axiosInstance
  )
  const customer = fetchedCustomerDetails?.data?.customer

  const { data: fetchedAppUsers }: SWRResponse = useSWR('/app-users', axiosInstance)
  const appUsers = fetchedAppUsers?.data?.appUsers

  // Style update function using server route
  const updateStyle = async (styleData: any) => {
    const response = await axiosInstance.post(`/customer-style/${id}`, { styleData })
    return response.data
  }

  // contacts
  const [componentRendered, setComponentRendered] = useState(false)
  const [phones, setPhones] = useState<string[]>([])
  const [emails, setEmails] = useState<string[]>([])
  const [leadEmails, setLeadEmails] = useState<string[]>([])

  const [defaultInvoiceEmail, setDefaultInvoiceEmail] = useState('')
  const [defaultPreviewEmail, setDefaultPreviewEmail] = useState('')
  const [defaultEmail, setDefaultEmail] = useState('')

  // Style state management
  const [styleData, setStyleData] = useState<StyleData>({
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    backgroundColor: '',
    textColor: '',
    fontColor: '',
  })

  const handleStyleChange = (field: keyof StyleData, value: string) => {
    setStyleData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Initialize style data when customer data loads
  useEffect(() => {
    if (customer?.style) {
      setStyleData({
        primaryColor: customer.style.primaryColor || '',
        secondaryColor: customer.style.secondaryColor || '',
        accentColor: customer.style.accentColor || '',
        backgroundColor: customer.style.backgroundColor || '',
        textColor: customer.style.textColor || '',
        fontColor: customer.style.fontColor || '',
      })
    }
  }, [customer?.style])

  useEffect(() => {
    if (customer?.emails && !componentRendered) {
      setEmails(JSON.parse(customer.emails))

      setDefaultEmail(customer.defaultEmail)
      setDefaultPreviewEmail(customer.defaultPreviewEmail)
      setDefaultInvoiceEmail(customer.defaultInvoiceEmail)

      setComponentRendered(true)
    }
    if (customer?.leadEmails) setLeadEmails(JSON.parse(customer.leadEmails))
    if (customer?.phoneNumbers) setPhones(JSON.parse(customer.phoneNumbers))
  }, [componentRendered, customer])

  const { data: resellerCustomersData }: SWRResponse = useSWR(
    '/customers?customerType=partner',
    axiosInstance
  )
  const resellerCustomers = resellerCustomersData?.data?.customers || []

  const onDropLogoFile = useCallback((acceptedFiles: [null] | File[]) => {
    // todo
    if (acceptedFiles?.length) {
      if (acceptedFiles[0]) {
        const sizeError = validateFileSize([acceptedFiles[0]])
        if (sizeError) {
          setFormikErrors([sizeError])
          setErrorsModalOpen(true)
          return
        }
      }
      setLogoFile(acceptedFiles[0])
      if (acceptedFiles[0] && acceptedFiles[0].name) {
        setLogoFileName(acceptedFiles[0].name)

        // blob to base64
        const reader = new FileReader()
        reader.readAsDataURL(acceptedFiles[0])
        reader.onloadend = () => {
          setLogoUrl(reader.result as string)
        }
        // const src = `data:audio/mp3;base64,${data}`
        // setLogoUrl(URL.createObjectURL(src))
      }
    }
  }, [])

  const {
    acceptedFiles: acceptedFilesLogo,
    getRootProps: getRootPropsLogo,
    getInputProps: getInputPropsLogo,
    isDragActive: isDragActiveLogo,
  } = useDropzone({
    onDrop: onDropLogoFile,
  })

  const validationSchema = customerValidationSchema

  function removeLogo() {
    setLogoFile(null)
    setLogoFileName('')
  }
  async function fetchLogo(url: string) {
    const fetched = await axios({ method: 'GET', url: fixImgSrc(url), responseType: 'blob' }) //
    setLogoFile(fetched.data)
  }

  React.useEffect(() => {
    if (customer?.type) setCustomerType(customer.type)
  }, [customer?.type])

  React.useEffect(() => {
    if (customer?.logo) {
      fetchLogo(customer.logo.url)
      setLogoFileName('customer-logo')
      setLogoUrl(customer.logo.url)
    }
  }, [customer?.logo, customer?.logo?.url])

  const handleNewEmailApprove = useCallback(() => {
    setEmails([...emails, newEmail])
  }, [emails, newEmail])
  const handleNewPhoneApprove = useCallback(() => {
    setPhones([...phones, newPhone])
  }, [phones, newPhone])
  const [addNewLeadEmailClicked, setAddNewLeadEmailClicked] = useState(false)
  const [newLeadEmail, setNewLeadEmail] = useState('')
  const handleNewLeadEmailApprove = useCallback(() => {
    setLeadEmails([...leadEmails, newLeadEmail])
  }, [leadEmails, newLeadEmail])

  const formik = useFormik({
    initialValues: {
      name: customer?.name || '',
      url: customer?.url || '',
      address: customer?.address || '',
      adAccountId: customer?.adAccountId || '',
      type: customer?.type || customerTypes[0].value,
      connectedReseller: customer?.connectedReseller?.id || '',
      appUser: customer?.appUser?.map((u: { id: string }) => u.id) || ([] as string[]),
      logo: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const newFormData: TcreatCustomerObject = {
        name: values.name,
        url: values.url,
        phoneNumbers: JSON.stringify(phones),
        emails: JSON.stringify(emails),
        leadEmails: JSON.stringify(leadEmails),
        adAccountId: values.adAccountId,
        defaultEmail,
        defaultInvoiceEmail,
        defaultPreviewEmail,
        address: values.address,
        type: values.type,
        connectedReseller: null,
        appUser:
          values.appUser && values.appUser.length > 0
            ? { set: values.appUser.map((id) => ({ id })) }
            : { set: [] },
        logo: null,
      }
      if (logoFile) {
        newFormData.logo = {
          upload: logoFile,
        }
      }

      if (!businessCustomer) {
        newFormData.type = values.type
      }

      if (values.type === 'resellerCustomer') {
        newFormData.connectedReseller = { connect: { id: values.connectedReseller } }
      } else {
        newFormData.connectedReseller = { disconnect: true }
      }

      // Handle style updates only if user has set any colors
      const hasStyleData = Object.values(styleData).some((color) => color.trim() !== '')
      if (hasStyleData) {
        if (customer?.style?.id) {
          // Update existing style - will be handled separately via updateStyle mutation
          newFormData.style = null
        } else {
          // Create new style
          const styleCreateData: any = {}
          if (styleData.primaryColor.trim()) styleCreateData.primaryColor = styleData.primaryColor
          if (styleData.secondaryColor.trim())
            styleCreateData.secondaryColor = styleData.secondaryColor
          if (styleData.accentColor.trim()) styleCreateData.accentColor = styleData.accentColor
          if (styleData.backgroundColor.trim())
            styleCreateData.backgroundColor = styleData.backgroundColor
          if (styleData.textColor.trim()) styleCreateData.textColor = styleData.textColor
          if (styleData.fontColor.trim()) styleCreateData.fontColor = styleData.fontColor

          newFormData.style = {
            create: styleCreateData,
          }
        }
      }
      const graphqlData = {
        variables: { data: newFormData, where: { id } },
      }
      const formData = new FormData()
      formData.append('graphqlData', JSON.stringify(graphqlData))
      if (logoFile) formData.append('logo', logoFile)
      try {
        const res = await axiosInstance.post(`/customer/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: { id },
        })
        if (res.data?.updateCustomer?.name) {
          // Handle style update if customer already has a style and user has set colors
          if (customer?.style?.id && hasStyleData) {
            try {
              const styleUpdateData: any = {}
              if (styleData.primaryColor.trim())
                styleUpdateData.primaryColor = styleData.primaryColor
              if (styleData.secondaryColor.trim())
                styleUpdateData.secondaryColor = styleData.secondaryColor
              if (styleData.accentColor.trim()) styleUpdateData.accentColor = styleData.accentColor
              if (styleData.backgroundColor.trim())
                styleUpdateData.backgroundColor = styleData.backgroundColor
              if (styleData.textColor.trim()) styleUpdateData.textColor = styleData.textColor
              if (styleData.fontColor.trim()) styleUpdateData.fontColor = styleData.fontColor

              await updateStyle(styleUpdateData)
            } catch (styleError) {
              console.error('Style update error:', styleError)
              // Don't fail the whole operation if style update fails
            }
          }

          setSuccessModalOpen(true)
          dispatch(setTriggerCustomersRefetch(true))
        }
      } catch (error) {
        const err = error as TunsolvedAny
        setFormikErrors([err.response?.data?.message])
        setErrorsModalOpen(true)
      }
      setIsLoading(false)
    },
  })

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (isSaveForDialogTriggered) {
      const errors = Object.values(formik.errors)
      if (errors.length) {
        setErrorsModalOpen(true)
        setFormikErrors(errors as string[])
        return
      }
      if (logoFile) await formik.setFieldValue('invoice', { upload: logoFile })

      formik.handleSubmit()
    }
  }

  useEffect(() => {
    if (isSaveForDialogTriggered) {
      formik.handleSubmit()
      dispatch(setIsSaveForDialogTriggered(false))
    }
  }, [dispatch, formik, isSaveForDialogTriggered])

  if (loadingCustomerDetails || isLoading) return <Loading /> // todo: fix double loading spinner on mutation
  if (loadingCustomerDetails) return <Loading />

  return (
    <form onSubmit={handleSubmit}>
      <div className="formContainer">
        <Container maxWidth="md" className="customerDetails subFormContainer">
          <div className="header">
            <FormattedMessage id="customer-details" />
          </div>
          <div className="customerWithLogo">
            <div className="fields">
              <Grid container className="content">
                {permission('name') && customer.name && (
                  <TextField
                    fullWidth
                    label={intl.formatMessage({ id: 'customer-name' })}
                    id="name"
                    name="name"
                    placeholder={intl.formatMessage({ id: 'customer-name' })}
                    type="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name ? String(formik.errors.name || '') : undefined}
                    defaultValue={customer?.name}
                  />
                )}
                {permission('type') && !businessCustomer && (
                  <FormControl
                    // sx={{ marginBottom: businessCustomer ? 2 : 'inherit' }}
                    fullWidth
                  >
                    <InputLabel id="type">
                      <FormattedMessage id="type" />
                    </InputLabel>
                    <Select
                      fullWidth
                      labelId="type"
                      id="type"
                      name="type"
                      label={intl.formatMessage({ id: 'type' })}
                      onChange={(e) => {
                        formik.handleChange(e)
                        setCustomerType(e.target.value)
                      }}
                      defaultValue={customer.type}
                    >
                      {Object.keys(CUSTOMER_TYPES).map((key) => (
                        <MenuItem key={key} value={CUSTOMER_TYPES[key].value}>
                          {CUSTOMER_TYPES[key].label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {permission('connectedReseller') &&
                  customer &&
                  customerType === 'resellerCustomer' && (
                    <FormControl
                      // sx={{ paddingBottom: businessCustomer ? 2 : 'inherit' }}
                      fullWidth
                    >
                      <InputLabel id="connectedReseller">
                        <FormattedMessage id="connected-reseller" />
                      </InputLabel>
                      <Select
                        fullWidth
                        labelId="connectedReseller"
                        id="connectedReseller"
                        name="connectedReseller"
                        label={intl.formatMessage({ id: 'connected-reseller' })}
                        onChange={formik.handleChange}
                        defaultValue={customer.connectedReseller?.id}
                      >
                        {resellerCustomers.map((i: { name: string; id: string }) => (
                          <MenuItem key={i.id} value={i.id}>
                            {i.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                {/* {permission('connectedAppUser') && ( */}
                <Autocomplete
                  multiple
                  fullWidth
                  options={appUsers || []}
                  getOptionLabel={(option: { firstName: string; email: string; id: string }) =>
                    `${option.firstName} ${option.firstName && '-'} ${option.email}`
                  }
                  onChange={(e, value) => {
                    formik.setFieldValue(
                      'appUser',
                      value.map((user) => user.id)
                    )
                  }}
                  defaultValue={customer?.appUser || []}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<FormattedMessage id="appUser" />}
                      id="appUser"
                      name="appUser"
                      placeholder="App Users"
                      error={formik.touched.appUser && Boolean(formik.errors.appUser)}
                    />
                  )}
                />
                {/* )} */}

                {permission('address') && customer.name && (
                  <TextField
                    fullWidth
                    label={intl.formatMessage({ id: 'address' })}
                    id="address"
                    name="address"
                    placeholder={intl.formatMessage({ id: 'address' })}
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={
                      formik.touched.address ? String(formik.errors.address || '') : undefined
                    }
                    defaultValue={customer.address}
                  />
                )}

                {permission('url') && !businessCustomer && (
                  <TextField
                    fullWidth
                    label="Url"
                    id="url"
                    name="url"
                    placeholder="https://example.com"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.url && Boolean(formik.errors.url)}
                    helperText={formik.touched.url ? String(formik.errors.url || '') : undefined}
                    defaultValue={customer.url}
                  />
                )}
                {(role === USER_ROLES.ADMIN.value || role === USER_ROLES.INTERNAL_USER.value) && (
                  <TextField
                    fullWidth
                    id="adAccountId"
                    name="adAccountId"
                    placeholder={intl.formatMessage({ id: 'ad-account-id' })}
                    type="adAccountId"
                    onChange={formik.handleChange}
                    error={formik.touched.adAccountId && Boolean(formik.errors.adAccountId)}
                    defaultValue={customer.adAccountId}
                  />
                )}
              </Grid>
            </div>
            {permission('logo') && (
              <div className="logo">
                <div className="content">
                  <FileUploadArea
                    title="Logo"
                    getInputProps={getInputPropsLogo}
                    getRootProps={getRootPropsLogo}
                    acceptedFiles={acceptedFilesLogo}
                    isDragActive={isDragActiveLogo}
                    // withResults
                  />
                </div>
                {customer.logo?.url && logoFile && (
                  <div className="thumbnailContainer">
                    <FileViewRow
                      fileSource={logoUrl}
                      onRemove={removeLogo}
                      fileName={logoFileName}
                      hasPreview
                      fixSrc={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {permission('emails') && (
            <>
              <div className="header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormattedMessage id="email" />
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="new-lead-notifications" />
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    customer?.type === 'resellerCustomer'
                      ? "These emails are used for new lead notifications. Notifications will also be sent to the parent reseller's emails."
                      : 'These emails are used for new lead notifications.'
                  }
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: '4px' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <Grid container className="content">
                {emails[0] ? (
                  emails.map((email) => (
                    <EmailRow
                      email={email}
                      emails={emails}
                      setEmails={setEmails}
                      key={email}
                      defaultPreviewEmail={defaultPreviewEmail}
                      setDefaultPreviewEmail={setDefaultPreviewEmail}
                      defaultEmail={defaultEmail}
                      setDefaultEmail={setDefaultEmail}
                    />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewEmailClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="email"
                          setContact={setNewEmail}
                          setItemClicked={() => setAddNewEmailClicked(false)}
                          handleApprove={() => handleNewEmailApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem item="E-mail" handleAddItem={() => setAddNewEmailClicked(true)} />
                </div>
              </Grid>
            </>
          )}

          {permission('phoneNumbers') && (
            <>
              <div className="header">
                <FormattedMessage id="phone" />
              </div>
              <Grid container className="content">
                {phones.length ? (
                  phones.map((phone) => (
                    <EditPhoneRow key={phone} phone={phone} setPhones={setPhones} />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewPhoneClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="phone"
                          setContact={setNewPhone}
                          setItemClicked={() => setAddNewPhoneClicked(false)}
                          handleApprove={() => handleNewPhoneApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem
                    item="Phone number"
                    handleAddItem={() => setAddNewPhoneClicked(true)}
                  />
                </div>
              </Grid>
            </>
          )}

          {permission('emails') && customer?.type !== 'partner' && (
            <>
              <div className="header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormattedMessage id="lead-emails" />
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="cv-upload-notifications" />
                  </Typography>
                </Box>
                <Tooltip
                  title="These emails will receive CV Upload Notifications when applicants submit their resumes."
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ padding: '4px' }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <Grid container className="content">
                {leadEmails.length ? (
                  leadEmails.map((email) => (
                    <EditLeadEmailRow
                      key={email}
                      email={email}
                      emails={leadEmails}
                      setEmails={setLeadEmails}
                    />
                  ))
                ) : (
                  <div />
                )}
                <div className="newContactCont">
                  {addNewLeadEmailClicked && (
                    <div>
                      <div className="newEmail">
                        <CreateContactRow
                          type="email"
                          setContact={setNewLeadEmail}
                          setItemClicked={() => setAddNewLeadEmailClicked(false)}
                          handleApprove={() => handleNewLeadEmailApprove()}
                        />
                      </div>
                    </div>
                  )}
                  <AddNewItem
                    item="Lead E-mail"
                    handleAddItem={() => setAddNewLeadEmailClicked(true)}
                  />
                </div>
              </Grid>
            </>
          )}
        </Container>

        {/* Customer Style Section */}
        <CustomerStyleSection styleData={styleData} onStyleChange={handleStyleChange} />

        <FormErrorsModal
          errorsModalOpen={errorsModalOpen}
          formikErrors={formikErrors}
          setErrorsModalOpen={setErrorsModalOpen}
        />
        <SuccessModal
          successModalOpen={successModalOpen}
          handleClose={handleClose}
          setSuccessModalOpen={setSuccessModalOpen}
          message="Updated successfully"
        />
      </div>
    </form>
  )
}
